import { FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { type TrackAnnualPlanMeetingRequest } from '../../../models/requestObjects/AnnualPlanRequestObjects'
import { Modal } from '../../customComponents/Modal'
import { useState } from 'react'
import type { AnnualPlanMeeting } from '../../../models/AnnualPlanMeeting'
import { TrackMeeting } from '../../../services/MeetingScheduleService'
import { toast } from 'react-toastify'

interface Props {
  meeting: AnnualPlanMeeting
  onSuccess: () => Promise<void>
  onCancel: () => void
}

interface TrackMeetingFormValues {
  wasCompleted: boolean | null
  actualDollarAmount: string
}

export function TrackMeetingModal (props: Props): JSX.Element {
  const [formValues, setFormValues] = useState<TrackMeetingFormValues>({
    wasCompleted: props.meeting.wasCompleted ?? null,
    actualDollarAmount: props.meeting.actualDollarAmount?.toString() ?? ''
  })

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target

    if (name === 'wasCompleted') {
      setFormValues({
        ...formValues,
        wasCompleted: value === 'true'
      })
    } else {
      setFormValues({
        ...formValues,
        [name]: value
      })
    }
  }

  const handleSave = async (): Promise<void> => {
    if (formValues.wasCompleted === null) {
      toast.error('Please indicate whether the meeting was held.')
      return
    }

    const request: TrackAnnualPlanMeetingRequest = {
      id: props.meeting.id,
      wasCompleted: formValues.wasCompleted,
      actualDollarAmount: formValues.actualDollarAmount === '' ? null : parseFloat(formValues.actualDollarAmount)
    }

    const { success, errors } = await TrackMeeting(request)
    if (!success) {
      errors.forEach((error) => {
        toast.error(error)
      })
      return
    }
    await props.onSuccess()
  }

  return <Modal
    title='RCN Meeting Tracking'
    open={true}
    width='450px'
    data-testid='annualPlanningTeamModal'
    onClose={props.onCancel}
    onConfirm={handleSave}
    buttonClassName='modal-button'
    confirmButtonText='Save Changes'
    confirmButtonClassName='modal-confirm-button'
    cancelButtonText='Cancel'
    bodyContent={ <>
      <Typography variant='h5' sx={{ my: '.5em' }}>Meeting Info</Typography>
      <FormControl>
        <FormLabel id='demo-radio-buttons-group-label'>Was this meeting held?</FormLabel>
        <RadioGroup
          row
          value={formValues.wasCompleted == null ? null : formValues.wasCompleted ? 'true' : 'false'}
          onChange={handleFieldUpdate}
          aria-labelledby='demo-radio-buttons-group-label'
          defaultValue={formValues.wasCompleted == null ? null : formValues.wasCompleted ? 'true' : 'false'}
          name='wasCompleted'
        >
          <FormControlLabel data-testid='meetingHeldOption' value='true' control={<Radio />} label='Yes' />
          <FormControlLabel data-testid='meetingNotHeldOption' value='false' control={<Radio />} label='No' />
        </RadioGroup>
      </FormControl>
      <Typography variant='h5' sx={{ my: '.5em' }}>Budget</Typography>
      <Typography variant='body1'>How much funding was actually used?</Typography>
      <TextField
        sx={{ mt: '.75em', width: '250px' }}
        onChange={handleFieldUpdate}
        label='Enter Amount'
        data-testid='actualDollarAmount'
        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        name='actualDollarAmount'
        variant='filled'
        value={formValues.actualDollarAmount ?? ''}
      />
    </>
    }
  />
}
